import React, { useMemo, useState } from "react";
import "../Analytics.scss";
import "../../../components/layout/layouts.scss";
import ButtonControl from "../../../components/controls/ButtonControl";
import inactiveSize1 from "../../../content/icons/inactiveChartSize1x1.svg";
import inactiveSize2 from "../../../content/icons/inactiveChartSize2x1.svg";
import inactiveSize3 from "../../../content/icons/inactiveChartSize2x2.svg";
import activeSize1 from "../../../content/icons/activeChartSize1x1.svg";
import activeSize2 from "../../../content/icons/activeChartSize2x1.svg";
import activeSize3 from "../../../content/icons/activeChartSize2x2.svg";
import refreshIcon from "../../../content/icons/whiteRefreshIcon.svg";
import refreshIconDisabled from "../../../content/icons/disabledRefreshIcon.svg";
import { DialogControl } from "../../../components/controls/DialogControl";
import { InputField } from "../../../components/controls/InputField";
import {
  CheckboxControl,
  DatePickerControl,
  SelectListControl,
} from "../../../components/controls";
import { CheckPicker } from "rsuite";
import _ from "lodash";
import GroupOrganizationIcon from "../assets/images/GroupOrganization.svg";
import GroupDistrictIcon from "../assets/images/GroupDistrict.svg";
import GroupSchoolIcon from "../assets/images/GroupSchool.svg";
import SystemTypes from "../../../SystemTypes";
import Loading from "../../../components/Loading";
import moment from "moment";
import ChartControl from "../../../components/controls/ChartControl/ChartControl";
import { isChartValid, orderCharts } from "../../../utilities/AnalyticsUtils";

export default function ChartConfigurationDialog({
  openAddChartDialog,
  setOpenAddChartDialog,
  setChartConfig,
  analyticConfig,
  analyticMgr,
  chartConfig,
  setPreviewChartData,
  setPreviewChartKeys,
  setClusters,
  previewChartData,
  previewChartKeys,
  orgs,
  updateDistricts,
  districts,
  updateSchools,
  schools,
  clusters,
  schoolYears,
  charts,
  gridData,
  convertChartsToGrid,
  setGridData,
  setCharts,
  updateChartPositions,
  setOriginalCharts,
  setOriginalGridData,
}) {
  const [loadingChart, setLoadingChart] = useState(false);
  const [previewSelectedKey, setPreviewSelectedKey] = useState(null);

  const [analyticsTypeMap] = useState({
    AverageScoreByObserver: "Average Observer Rating",
    AverageScore: "Average Self-Reflection Rating",
    TeacherCertificationStatus: "Teacher Certification Status",
    ObservationStatus: "Observation Status",
    ObserverVsSelfScore: "Observer Rating vs. Self-Reflection Rating",
    RefinementGoal: "Refinement Goals",
    ReinforcementGoal: "Reinforcement Goals",
    ObservationCompleted: "Observations Completed",
    RequiredVsSubmittedObservation: "Required vs. Completed Observations",
  });

  const addChartToLayout = async () => {
    let updatedChartConfig;
    let editingId = chartConfig.editingMode && chartConfig.Id;

    if (!previewChartData) {
      await analyticMgr.retrieveChartData(chartConfig, editingId).then((r) => {
        if (typeof r === "object") {
          updatedChartConfig = { ...r };
        } else {
          updatedChartConfig = false;
        }
      });
    } else updatedChartConfig = { ...chartConfig };

    const spreadCharts =
      charts && gridData.length > 0 ? orderCharts(gridData, charts) : [];
    const chartIndex = _.findIndex(
      charts,
      (x) => x.Id === updatedChartConfig.Id
    );
    if (chartIndex !== -1) {
      spreadCharts.splice(chartIndex, 1, updatedChartConfig);
    } else spreadCharts.push(updatedChartConfig);

    let chartNullFields = _.map(spreadCharts, (x) => {
      return {
        ...x,
        Data: undefined,
        editingMode: undefined,
        failToLoad: undefined,
      };
    });
    if (analyticConfig) {
      const defaultLayoutConfig = { ...analyticConfig.defaultGroup };
      defaultLayoutConfig[analyticConfig.viewingMode] =
        JSON.stringify(chartNullFields);
      analyticMgr.saveDefaultConfigByGroup(
        analyticConfig.defaultGroupType,
        defaultLayoutConfig
      );
    } else {
      analyticMgr.saveChartLayout(chartNullFields);
    }

    if (analyticConfig) {
      setChartConfig({
        ...analyticMgr.DefaultItem,
        ClientIds: chartConfig.ClientIds,
        DistrictIds: chartConfig.DistrictIds,
        SchoolIds: chartConfig.SchoolIds,
        organizations: chartConfig.organizations,
        districts: chartConfig.districts,
        schools: chartConfig.schools,
        SchoolYearIdentifier: 0,
        defaultGroupType: chartConfig.defaultGroupType,
        defaultGroup: chartConfig.defaultGroup,
      });
    } else setChartConfig(analyticMgr.DefaultItem);

    setPreviewChartData(null);
    const grid = convertChartsToGrid(spreadCharts, true);
    setGridData(grid);
    setCharts(updateChartPositions(grid, spreadCharts, true));
    setOriginalCharts(spreadCharts);
    setOriginalGridData(grid);
    setOpenAddChartDialog(false);
  };

  const updateChartConfig = (value, field, label) => {
    const updatedChartConfig = { ...chartConfig };
    if (field === "useSpecificDates") {
      if (!value) {
        updatedChartConfig["BeginDate"] = "";
        updatedChartConfig["EndDate"] = "";
      }
    } else if (field === "ClientIds") {
      if (value.length === 0) {
        updatedChartConfig["SchoolIds"] = [];
        updatedChartConfig["schools"] = [];
        updatedChartConfig["DistrictIds"] = [];
        updatedChartConfig["districts"] = [];
        updatedChartConfig["ClusterIds"] = [];
        updatedChartConfig["clusters"] = [];
      }

      updatedChartConfig["selectedId"] = undefined;
      if (updatedChartConfig.organizations.includes(label)) {
        if (updatedChartConfig.organizations.length > 1) {
          updatedChartConfig["SchoolIds"] = [];
          updatedChartConfig["schools"] = [];
          updatedChartConfig["DistrictIds"] = [];
          updatedChartConfig["districts"] = [];
          updatedChartConfig["ClusterIds"] = [];
          updatedChartConfig["clusters"] = [];
        }
        _.remove(updatedChartConfig.organizations, (x) => x === label);
      } else updatedChartConfig.organizations.push(label);
    } else if (field === "DistrictIds") {
      if (value.length === 0) {
        updatedChartConfig["SchoolIds"] = [];
        updatedChartConfig["schools"] = [];
        updatedChartConfig["ClusterIds"] = [];
        updatedChartConfig["clusters"] = [];
      }
      updatedChartConfig["selectedId"] = undefined;
      if (updatedChartConfig.districts.includes(label)) {
        if (updatedChartConfig.districts.length > 1) {
          updatedChartConfig["SchoolIds"] = [];
          updatedChartConfig["schools"] = [];
          updatedChartConfig["ClusterIds"] = [];
          updatedChartConfig["clusters"] = [];
        }
        _.remove(updatedChartConfig.districts, (x) => x === label);
      } else updatedChartConfig.districts.push(label);
    } else if (field === "SchoolIds") {
      if (value.length === 0) {
        updatedChartConfig["ClusterIds"] = [];
        updatedChartConfig["clusters"] = [];
      }
      updatedChartConfig["selectedId"] = undefined;
      analyticMgr.grabGroup("Cluster", value).then((r) => setClusters(r));
      if (updatedChartConfig.schools.includes(label)) {
        if (updatedChartConfig.schools.length > 1) {
          updatedChartConfig["ClusterIds"] = [];
          updatedChartConfig["clusters"] = [];
        }
        _.remove(updatedChartConfig.schools, (x) => x === label);
      } else updatedChartConfig.schools.push(label);
    }
    updatedChartConfig[field] = value;

    updatedChartConfig["schools"] = [];
    updatedChartConfig["districts"] = [];
    updatedChartConfig["organizations"] = [];

    updatedChartConfig.SchoolIds?.forEach((s) => {
      let school = schools.find((x) => x.SchoolId === s);
      if (school) {
        updatedChartConfig["schools"].push(school.Name);
      }
    });

    updatedChartConfig.DistrictIds?.forEach((d) => {
      let district = districts.find((x) => x.DistrictId === d);
      if (district) {
        updatedChartConfig["districts"].push(district.Name);
      }
    });

    updatedChartConfig.ClientIds?.forEach((c) => {
      let org = orgs.find((x) => x.ClientId === c);
      if (org) {
        updatedChartConfig["organizations"].push(org.Name);
      }
    });

    setChartConfig(updatedChartConfig);
  };

  const createChart = async (isUpdate) => {
    if (isChartValid(chartConfig)) {
      setLoadingChart(true);
      await analyticMgr
        .retrieveChartData(
          chartConfig,
          chartConfig.editingMode && chartConfig.Id,
          isUpdate
        )
        .then((r) => {
          if (typeof r === "object") {
            const previewChart = { ...r };
            setChartConfig(r);
            if (r.keys) {
              var selectedIdValue = _.sortBy(
                r.keys ?? [],
                (x) => x.text
              ).first().value;
              setPreviewSelectedKey(selectedIdValue);
              setPreviewChartKeys(_.sortBy(r.keys, (x) => x.text));
              setPreviewChartData({
                ...previewChart,
                selectedId: selectedIdValue,
              });
            } else {
              setPreviewChartData(previewChart);
            }
          } else {
            setPreviewChartData("No Data");
          }
        });
      setLoadingChart(false);
    }
  };

  const updatePreviewChart = (value) => {
    setPreviewSelectedKey(value);
    setPreviewChartData({ ...previewChartData, selectedId: value });
    setChartConfig({ ...chartConfig, selectedId: value });
  };

  const memoizedChart = useMemo(
    () => (
      <ChartControl
        type={previewChartData?.chartType}
        data={previewChartData}
        fixedHeight={"100%"}
        fixedWidth={"100%"}
      />
    ),
    [previewChartData]
  );

  const hasOrgPermissions =
    analyticMgr?.AppUserState?.userProfile?.UserPositionType <
    SystemTypes.UserPositionType.DistrictAdmin;

  return (
    <DialogControl
      className={"analytic-create-dialog-outer"}
      innerclassName={"analytic-create-dialog"}
      openDialog={openAddChartDialog}
      title={"Chart Configuration"}
      disableOk={!isChartValid(chartConfig)}
      disableXButton
      onCancel={() => {
        setOpenAddChartDialog(false);
        setChartConfig(
          !analyticConfig
            ? analyticMgr.DefaultItem
            : {
                ...analyticMgr.DefaultItem,
                ClientIds: chartConfig.ClientIds,
                DistrictIds: chartConfig.DistrictIds,
                SchoolIds: chartConfig.SchoolIds,
                organizations: chartConfig.organizations,
                districts: chartConfig.districts,
                schools: chartConfig.schools,
              }
        );
        setPreviewChartData(null);
        setPreviewSelectedKey(null);
        setPreviewChartKeys(null);
      }}
      okText={chartConfig?.editingMode ? "Save" : "Add"}
      onOk={() => addChartToLayout()}
    >
      <div className="analytic-left-col">
        <InputField
          id="type"
          title="Analytic Type"
          value={chartConfig?.AnalyticType}
          fieldName="AnalyticType"
          disableError={true}
          onChange={(value, field) => updateChartConfig(value, field)}
        >
          <SelectListControl
            textValuePairs={Object.entries(SystemTypes.AnalyticType).map(
              ([type, value]) => {
                return { text: analyticsTypeMap[type], value: value };
              }
            )}
          />
        </InputField>
        <InputField
          title={"Analytic Name"}
          type="text"
          value={chartConfig?.Name}
          fieldName="Name"
          disableError={true}
          placeholder="Name"
          onChange={(value, field) => updateChartConfig(value, field)}
        ></InputField>
        <div className="size-selector-container">
          <h3>Size</h3>
          <div className="size-selectors">
            <div
              className="size-1"
              onClick={() => {
                updateChartConfig(1, "Size");
              }}
            >
              <img
                src={chartConfig?.Size === 1 ? activeSize1 : inactiveSize1}
                alt=""
              />
            </div>
            <div
              className="size-2"
              onClick={() => {
                updateChartConfig(2, "Size");
              }}
            >
              <img
                src={chartConfig?.Size === 2 ? activeSize2 : inactiveSize2}
                alt=""
              />
            </div>
            <div
              className="size-3"
              onClick={() => {
                updateChartConfig(3, "Size");
              }}
            >
              <img
                src={chartConfig?.Size === 3 ? activeSize3 : inactiveSize3}
                alt=""
              />
            </div>
          </div>
        </div>
        <div
          className={
            "chart-preview-container" +
            (!previewChartData ? " chart-preview-container-center" : "")
          }
        >
          {!previewChartData ? (
            <ButtonControl
              type={"okay"}
              loading={loadingChart}
              disabled={!isChartValid(chartConfig)}
              onClick={createChart}
            >
              Preview Chart
            </ButtonControl>
          ) : !loadingChart ? (
            <>
              <div className="chart-preview-header-wrapper">
                <h3 className="chart-preview-header">Chart Preview</h3>
                <div
                  className={
                    "chart-update-btn " +
                    (!isChartValid(chartConfig)
                      ? "chart-update-btn-disable"
                      : "")
                  }
                  onClick={() => createChart(true)}
                >
                  {" "}
                  <img
                    src={
                      !isChartValid(chartConfig)
                        ? refreshIconDisabled
                        : refreshIcon
                    }
                    alt="RefreshIcon"
                  />{" "}
                  Update
                </div>
              </div>
              <div className="chart-preview-chart-wrapper">
                {previewChartData === "No Data" ? (
                  <div className="center-text">{previewChartData}</div>
                ) : (
                  <>
                    {(previewChartData.AnalyticType == 7 ||
                      previewChartData.AnalyticType == 5 ||
                      previewChartData.AnalyticType == 6) &&
                      previewChartKeys?.length > 0 && (
                        <InputField
                          fieldName={"preview-chart"}
                          disableError={true}
                          value={
                            previewSelectedKey ??
                            previewChartKeys?.first()?.value
                          }
                          onChange={(value) => {
                            updatePreviewChart(value);
                          }}
                        >
                          <SelectListControl
                            hidePleaseSelect={true}
                            textValuePairs={previewChartKeys}
                          />
                        </InputField>
                      )}
                    {memoizedChart}
                  </>
                )}
              </div>
            </>
          ) : (
            <div className="center-text">
              {" "}
              <Loading size={"3rem"} />
            </div>
          )}
        </div>
      </div>
      <div className="analytic-right-col">
        <div className="chart-config-row">
          <h3>Display Data By</h3>
          <div className="display-by-group-btn-wrapper">
            {hasOrgPermissions && (
              <div
                className={
                  "group-btn" +
                  (chartConfig?.GroupBy === "Client" ? " group-btn-active" : "")
                }
                onClick={() => updateChartConfig("Client", "GroupBy")}
              >
                <img src={GroupOrganizationIcon} alt="OrgIcon" />
                Organization
              </div>
            )}
            <div
              className={
                "group-btn" +
                (chartConfig?.GroupBy === "District" ? " group-btn-active" : "")
              }
              onClick={() => updateChartConfig("District", "GroupBy")}
            >
              <img src={GroupDistrictIcon} alt="DistrictIcon" />
              District
            </div>
            <div
              className={
                "group-btn" +
                (chartConfig?.GroupBy === "School" ? " group-btn-active" : "")
              }
              onClick={() => updateChartConfig("School", "GroupBy")}
            >
              <img src={GroupSchoolIcon} alt="SchoolIcon" />
              School
            </div>
          </div>
        </div>
        <div className="chart-config-row">
          <h3>Filters</h3>
          {hasOrgPermissions && (
            <>
              {orgs ? (
                <InputField
                  title="Organization"
                  disableError={true}
                  value={chartConfig?.ClientIds ?? []}
                  fieldName={"OrGS"}
                  key={"organizations"}
                  isDisabled={analyticConfig && true}
                >
                  <CheckPicker
                    id="organizations"
                    key="organizations"
                    labelKey="FullName"
                    valueKey="OrgId"
                    onClean={() => updateChartConfig([], "ClientIds")}
                    onSelect={(value, label) => {
                      updateChartConfig(value, "ClientIds", label.FullName);
                      updateDistricts(value);
                    }}
                    value={chartConfig?.ClientIds ?? []}
                    data={orgs ?? []}
                  />
                </InputField>
              ) : (
                <Loading />
              )}
            </>
          )}
          <InputField
            title="District"
            key="districts"
            isDisabled={
              chartConfig?.ClientIds?.length === 0 || (analyticConfig && true)
            }
            value={chartConfig?.DistrictIds ?? []}
            disableError={true}
          >
            <CheckPicker
              id="districts"
              title="District"
              labelKey="FullName"
              onClean={() => updateChartConfig([], "DistrictIds")}
              valueKey="DistrictId"
              value={chartConfig?.DistrictIds ?? []}
              onSelect={async (value, label) => {
                updateChartConfig(value, "DistrictIds", label.FullName);
                updateSchools(value);
              }}
              data={districts ?? []}
            />
          </InputField>
          <InputField
            title="School"
            key="schools"
            isDisabled={
              chartConfig?.DistrictIds?.length === 0 || (analyticConfig && true)
            }
            value={chartConfig?.SchoolIds ?? []}
            disableError={true}
          >
            <CheckPicker
              id="schools"
              title="School"
              onClean={() => updateChartConfig([], "SchoolIds")}
              key="schools"
              labelKey="FullName"
              valueKey="SchoolId"
              value={chartConfig?.SchoolIds ?? []}
              onSelect={async (value, label) => {
                updateChartConfig(value, "SchoolIds", label.FullName);
              }}
              data={schools ?? []}
            />
          </InputField>
          <InputField
            value={chartConfig?.useClusters}
            isDisabled={chartConfig?.SchoolIds?.length === 0 || analyticConfig}
            disableError={true}
          >
            <CheckboxControl
              className={"cluster-checkbox"}
              checked={chartConfig?.useClusters}
              onChange={(e) => {
                updateChartConfig(e.target.value, "useClusters");
              }}
            />
          </InputField>
          <InputField
            title="Clusters"
            key="clusters"
            value={chartConfig?.ClusterIds ?? []}
            isDisabled={!chartConfig?.useClusters}
            disableError
          >
            <CheckPicker
              id="clusters"
              key="clusters"
              labelKey="Name"
              onClean={() => updateChartConfig([], "ClusterIds")}
              valueKey="Id"
              value={chartConfig?.ClusterIds ?? []}
              onSelect={(value) => {
                updateChartConfig(value, "ClusterIds");
              }}
              data={clusters ?? []}
            />
          </InputField>
        </div>
        {!analyticConfig && (
          <div className="chart-config-row">
            <h3>Time</h3>
            <InputField
              title="School Year"
              fieldName={"SchoolYearIdentifier"}
              isDisabled={chartConfig?.useSpecificDates}
              value={chartConfig?.SchoolYearIdentifier}
              onChange={(value, fieldName) =>
                updateChartConfig(value, fieldName)
              }
              disableError={true}
            >
              <SelectListControl textValuePairs={schoolYears} />
            </InputField>
            <InputField
              value={chartConfig?.useSpecificDates}
              disableError={true}
            >
              <CheckboxControl
                className={"date-checkbox"}
                checked={chartConfig?.useSpecificDates}
                onChange={(e) => {
                  updateChartConfig(e.target.value, "useSpecificDates");
                }}
              />
            </InputField>
            <div className="date-wrapper">
              <InputField
                disableError={true}
                fieldName={"BeginDate"}
                isDisabled={!chartConfig?.useSpecificDates}
                value={
                  moment(chartConfig?.BeginDate).local().format("YYYY-MM-DD") ??
                  ""
                }
                onChange={(value, fieldName) =>
                  updateChartConfig(value, fieldName)
                }
              >
                <DatePickerControl />
              </InputField>
              <InputField
                disableError={true}
                fieldName={"EndDate"}
                isDisabled={!chartConfig?.useSpecificDates}
                value={
                  moment(chartConfig?.EndDate).local().format("YYYY-MM-DD") ??
                  ""
                }
                onChange={(value, fieldName) =>
                  updateChartConfig(value, fieldName)
                }
              >
                <DatePickerControl />
              </InputField>
            </div>
          </div>
        )}
      </div>
    </DialogControl>
  );
}
